import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  getTeacherSettlement,
  SettlementBreakdown,
  TeacherSettlement as TeacherSettlementModel,
} from '../api/settlement';
import { formatMoney } from '../../../commodity/format/money';
import { PortalPage } from '../../../design-system/portal';

const fmt = (iso?: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
};

const Stat: React.FC<{ label: string; value: string; color: string }> = ({
  label,
  value,
  color,
}) => (
  <div
    style={{
      border: '1px solid #d1d5da',
      borderRadius: 8,
      padding: 16,
      minWidth: 140,
      flex: 1,
    }}
  >
    <div style={{ fontSize: 12, color: '#666' }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
  </div>
);

const holdbackLabel = (h: SettlementBreakdown['holdback']): string => {
  if (h.released) return `Liberada · ${fmt(h.releasedAt)}`;
  if (h.inWindow) {
    const hours = Math.floor(h.windowMsRemaining / 1000 / 3600);
    return `En ventana · faltan ${hours}h`;
  }
  if (h.amount === 0) return '—';
  return 'Pendiente';
};

/**
 * MC-03 — the sessions the teacher did not give, and the holdback that results.
 *
 * A refunded session is shown as refunded to the family: never paid, never
 * delivered, and not taken from the teacher a second time. The holdback is
 * shown on the eligible base, with the target and the releasable balance kept
 * apart, because they are different numbers (MC03-R16).
 */
const RefundedSessions: React.FC<{ breakdown: SettlementBreakdown }> = ({ breakdown }) => {
  const sessions = breakdown.refundedSessions ?? [];
  const h = breakdown.holdbackSettlement;
  if (sessions.length === 0 && h?.status !== 'HELD') return null;
  const money = (n: number) => formatMoney(n, breakdown.currency);
  return (
    <div
      aria-label="Sesiones no impartidas"
      style={{ marginTop: 10, fontSize: 13, borderTop: '1px solid #e9ecef', paddingTop: 8 }}
    >
      {sessions.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {sessions.map((s) => (
            <li key={s.sequence}>
              Sesión {s.sequence}: no impartida.{' '}
              {s.refundedAt
                ? `Se devolvieron ${money(s.amount)} a la familia.`
                : 'La devolución a la familia está en proceso.'}{' '}
              No se te paga y no se descuenta de lo que ya recibiste.
            </li>
          ))}
        </ul>
      )}
      {h && sessions.length > 0 && (
        <p style={{ margin: '6px 0 0', color: '#495057' }}>
          Base elegible {money(h.eligibleBase)} · Retención objetivo {money(h.targetHoldback)} ·
          Saldo liberable {money(h.releasable)}
        </p>
      )}
      {h?.status === 'HELD' && (
        <p role="status" style={{ margin: '6px 0 0', color: '#e8590c' }}>
          Retención en revisión por {h.owner ?? 'Operaciones'}. {h.nextAction}
        </p>
      )}
    </div>
  );
};

/**
 * PI-3 Epic 2 S2.1 — teacher settlement view. Adds `refunded` and
 * `netEarned` on top of the PI-2 earnings shape so teachers see what
 * actually settled to them after refunds.
 */
const TeacherSettlement: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<TeacherSettlementModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getTeacherSettlement();
        if (!cancelled) setData(res);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'No se pudo cargar la liquidación');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const exportCsv = () => {
    if (!data) return;
    const header =
      'enrollmentId,courseId,state,released,pending,holdbackAmount,holdbackReleased,refunded,netEarned';
    const rows = data.enrollments.map((e) =>
      [
        e.enrollmentId,
        e.courseId,
        e.state,
        e.released,
        e.pending,
        e.holdback.amount,
        e.holdback.released ? 'true' : 'false',
        e.refunded,
        e.netEarned,
      ].join(','),
    );
    const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `settlement-${data.teacherId}-${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  if (loading) return <p>Cargando tu liquidación...</p>;
  if (error) return <p style={{ color: '#c92a2a' }}>{error}</p>;
  if (!data) return <p>Sin datos de liquidación.</p>;

  return (
    <PortalPage screenId="s_settlement">
      <div style={{ maxWidth: 880, margin: '0 auto' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <h1>Mi liquidación</h1>
          <button
            onClick={exportCsv}
            disabled={data.enrollments.length === 0}
            style={{
              border: '1px solid #1971c2',
              background: '#1971c2',
              color: '#fff',
              borderRadius: 6,
              padding: '6px 12px',
              cursor: 'pointer',
            }}
          >
            Descargar CSV
          </button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12 }}>
          <Stat
            label="Liberado"
            value={formatMoney(data.totals.released, data.currency)}
            color="#2b8a3e"
          />
          <Stat
            label="Pendiente"
            value={formatMoney(data.totals.pending, data.currency)}
            color="#1971c2"
          />
          <Stat
            label="En retención"
            value={formatMoney(data.totals.holdback, data.currency)}
            color="#e8590c"
          />
          <Stat
            label="Reembolsado"
            value={formatMoney(data.totals.refunded, data.currency)}
            color="#c92a2a"
          />
          <Stat
            label="Neto"
            value={formatMoney(data.totals.netEarned, data.currency)}
            color="#222"
          />
        </div>

        <h2 style={{ marginTop: 24 }}>Por inscripción</h2>
        {data.enrollments.length === 0 && (
          <p style={{ color: '#666' }}>Aún no tienes inscripciones.</p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {data.enrollments.map((e) => (
            <div
              key={e.enrollmentId}
              style={{ border: '1px solid #d1d5da', borderRadius: 8, padding: 14 }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 8,
                }}
              >
                <div>
                  <strong>Inscripción {e.enrollmentId.slice(0, 8)}</strong>
                  <span style={{ color: '#666', fontSize: 12, marginLeft: 8 }}>{e.state}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 13, color: '#2b8a3e', fontWeight: 700 }}>
                    Neto: {formatMoney(e.netEarned, e.currency)}
                  </span>
                  <button
                    onClick={() => navigate(`/enrollments/${e.enrollmentId}`)}
                    style={{
                      border: '1px solid #1971c2',
                      background: '#fff',
                      color: '#1971c2',
                      borderRadius: 4,
                      padding: '6px 10px',
                      fontSize: 12,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Ver inscripción →
                  </button>
                </div>
              </div>
              <div
                style={{
                  marginTop: 10,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                  gap: 8,
                  fontSize: 13,
                }}
              >
                <div>
                  <div style={{ color: '#666', fontSize: 11 }}>Liberado</div>
                  <div style={{ fontWeight: 600 }}>{formatMoney(e.released, e.currency)}</div>
                </div>
                <div>
                  <div style={{ color: '#666', fontSize: 11 }}>Pendiente</div>
                  <div style={{ fontWeight: 600 }}>{formatMoney(e.pending, e.currency)}</div>
                </div>
                <div>
                  <div style={{ color: '#666', fontSize: 11 }}>Retención</div>
                  <div style={{ fontWeight: 600 }}>
                    {formatMoney(e.holdback.amount, e.currency)}
                  </div>
                  <div style={{ color: '#666', fontSize: 11 }}>{holdbackLabel(e.holdback)}</div>
                </div>
                <div>
                  <div style={{ color: '#666', fontSize: 11 }}>Reembolsado</div>
                  <div style={{ fontWeight: 600 }}>{formatMoney(e.refunded, e.currency)}</div>
                </div>
              </div>
              <RefundedSessions breakdown={e} />
            </div>
          ))}
        </div>
      </div>
    </PortalPage>
  );
};

export default TeacherSettlement;
